
// Travel distance and time mappings for destinations from the Netherlands

export interface TravelDistance {
  flightTimeHours: number;
  drivingTimeHours?: number;
  drivingDistanceKm?: number;
  departureAirport?: string;
}

// Flight times from Schiphol (AMS) to major destinations
export const FLIGHT_TIMES: Record<string, TravelDistance> = {
  // Nederland (domestic)
  'Nederland': { flightTimeHours: 0, drivingTimeHours: 0, drivingDistanceKm: 0 },

  // Short-haul flights (0-3 hours)
  'België': { flightTimeHours: 1, drivingTimeHours: 2, drivingDistanceKm: 200 },
  'Duitsland': { flightTimeHours: 1.5, drivingTimeHours: 3, drivingDistanceKm: 300 },
  'Frankrijk': { flightTimeHours: 1.5, drivingTimeHours: 5, drivingDistanceKm: 500 },
  'Verenigd Koninkrijk': { flightTimeHours: 1.5 },
  'Denemarken': { flightTimeHours: 1.5, drivingTimeHours: 6, drivingDistanceKm: 600 },
  'Oostenrijk': { flightTimeHours: 2, drivingTimeHours: 8, drivingDistanceKm: 800 },
  'Zwitserland': { flightTimeHours: 2, drivingTimeHours: 7, drivingDistanceKm: 700 },
  'Tsjechië': { flightTimeHours: 2, drivingTimeHours: 8, drivingDistanceKm: 800 },
  'Polen': { flightTimeHours: 2, drivingTimeHours: 10, drivingDistanceKm: 1000 },
  'Italië': { flightTimeHours: 2.5, drivingTimeHours: 12, drivingDistanceKm: 1200 },
  'Spanje': { flightTimeHours: 2.5, drivingTimeHours: 13, drivingDistanceKm: 1300 },
  'Portugal': { flightTimeHours: 2.5, drivingTimeHours: 15, drivingDistanceKm: 1500 },
  'Kroatië': { flightTimeHours: 2.5, drivingTimeHours: 12, drivingDistanceKm: 1200 },
  'Slovenië': { flightTimeHours: 2, drivingTimeHours: 10, drivingDistanceKm: 1000 },
  'Hongarije': { flightTimeHours: 2.5, drivingTimeHours: 10, drivingDistanceKm: 1000 },
  'Slowakije': { flightTimeHours: 2, drivingTimeHours: 9, drivingDistanceKm: 900 },
  'Noorwegen': { flightTimeHours: 2 },
  'Zweden': { flightTimeHours: 2 },
  'Finland': { flightTimeHours: 2.5 },
  'Ierland': { flightTimeHours: 2 },

  // Medium-haul flights (3-6 hours)
  'Griekenland': { flightTimeHours: 3.5 },
  'Turkije': { flightTimeHours: 4 },
  'Cyprus': { flightTimeHours: 4.5 },
  'Malta': { flightTimeHours: 3 },
  'Bulgarije': { flightTimeHours: 3 },
  'Roemenië': { flightTimeHours: 3 },
  'Marokko': { flightTimeHours: 3.5 },
  'Tunesië': { flightTimeHours: 3 },
  'Egypte': { flightTimeHours: 4.5 },
  'Jordanië': { flightTimeHours: 5 },
  'Israël': { flightTimeHours: 4.5 },
  'Rusland': { flightTimeHours: 3.5 },
  'Georgië': { flightTimeHours: 5 },
  'Armenië': { flightTimeHours: 5 },

  // Long-haul flights (6+ hours)
  'Verenigde Staten': { flightTimeHours: 8 },
  'Canada': { flightTimeHours: 8 },
  'Mexico': { flightTimeHours: 11 },
  'Costa Rica': { flightTimeHours: 12 },
  'Cuba': { flightTimeHours: 10 },
  'Dominicaanse Republiek': { flightTimeHours: 9 },
  'Barbados': { flightTimeHours: 9 },
  'Curaçao': { flightTimeHours: 9 },
  'Aruba': { flightTimeHours: 9 },
  'Thailand': { flightTimeHours: 11 },
  'Vietnam': { flightTimeHours: 12 },
  'Cambodja': { flightTimeHours: 12 },
  'Indonesië': { flightTimeHours: 13 },
  'Maleisië': { flightTimeHours: 12 },
  'Singapore': { flightTimeHours: 12 },
  'Filipijnen': { flightTimeHours: 13 },
  'China': { flightTimeHours: 10 },
  'Japan': { flightTimeHours: 11 },
  'Zuid-Korea': { flightTimeHours: 10 },
  'India': { flightTimeHours: 8 },
  'Sri Lanka': { flightTimeHours: 10 },
  'Maldiven': { flightTimeHours: 10 },
  'Seychellen': { flightTimeHours: 10 },
  'Mauritius': { flightTimeHours: 11 },
  'Kenia': { flightTimeHours: 8 },
  'Tanzania': { flightTimeHours: 9 },
  'Zuid-Afrika': { flightTimeHours: 11 },
  'Australië': { flightTimeHours: 22 },
  'Nieuw-Zeeland': { flightTimeHours: 24 },
  'Brazilië': { flightTimeHours: 12 },
  'Argentinië': { flightTimeHours: 14 },
  'Peru': { flightTimeHours: 13 },
  'Chili': { flightTimeHours: 15 }
};

export interface TravelConstraints {
  maxFlightHours?: number;
  maxDrivingHours?: number;
  maxDrivingKm?: number;
  preferDriving?: boolean;
  preferFlying?: boolean;
}

export function detectTravelConstraints(searchTerms: string[]): TravelConstraints | null {
  const query = searchTerms.join(' ').toLowerCase();
  
  // Flight time constraints
  const flightTimeMatch = query.match(/(?:maximaal|max|binnen|onder)\s+(\d+)\s*(?:uur|u)\s*(?:vliegen|vlucht|vlieg)/);
  if (flightTimeMatch) {
    return {
      maxFlightHours: parseInt(flightTimeMatch[1]),
      preferFlying: true
    };
  }

  // Driving time constraints
  const drivingTimeMatch = query.match(/(?:maximaal|max|binnen|onder)\s+(\d+)\s*(?:uur|u)\s*(?:rijden|rijdt|auto|drive)/);
  if (drivingTimeMatch) {
    return {
      maxDrivingHours: parseInt(drivingTimeMatch[1]),
      preferDriving: true
    };
  }

  // Driving distance constraints in km
  const drivingKmMatch = query.match(/(?:maximaal|max|binnen|onder)\s+(\d+)\s*(?:km|kilometer)/);
  if (drivingKmMatch) {
    return {
      maxDrivingKm: parseInt(drivingKmMatch[1]),
      preferDriving: true
    };
  }

  // General preference detection
  if (query.includes('dichtbij') || query.includes('nabij') || query.includes('kort vliegen')) {
    return { maxFlightHours: 3 };
  }

  if (query.includes('ver weg') || query.includes('verre reis') || query.includes('lang vliegen')) {
    return { maxFlightHours: 999 }; // No real constraint, just preference detection
  }

  if (query.includes('met de auto') || query.includes('rijden naar') || query.includes('roadtrip')) {
    return { preferDriving: true };
  }

  return null;
}

export function filterCountriesByTravelConstraints(
  countries: string[], 
  constraints: TravelConstraints
): string[] {
  return countries.filter(country => {
    const travelInfo = FLIGHT_TIMES[country];
    if (!travelInfo) return true; // Keep countries we don't have data for

    // Check flight time constraints
    if (constraints.maxFlightHours !== undefined) {
      if (travelInfo.flightTimeHours > constraints.maxFlightHours) {
        return false;
      }
    }

    // Check driving time constraints
    if (constraints.maxDrivingHours !== undefined && travelInfo.drivingTimeHours !== undefined) {
      if (travelInfo.drivingTimeHours > constraints.maxDrivingHours) {
        return false;
      }
    }

    // Check driving distance constraints
    if (constraints.maxDrivingKm !== undefined && travelInfo.drivingDistanceKm !== undefined) {
      if (travelInfo.drivingDistanceKm > constraints.maxDrivingKm) {
        return false;
      }
    }

    // Preference filtering
    if (constraints.preferDriving && travelInfo.drivingTimeHours === undefined) {
      return false; // Filter out non-driveable destinations
    }

    return true;
  });
}

export function getCountriesWithinTravelConstraints(constraints: TravelConstraints): string[] {
  return Object.keys(FLIGHT_TIMES).filter(country => {
    const travelInfo = FLIGHT_TIMES[country];
    
    // Check flight time constraints
    if (constraints.maxFlightHours !== undefined) {
      if (travelInfo.flightTimeHours > constraints.maxFlightHours) {
        return false;
      }
    }

    // Check driving constraints
    if (constraints.maxDrivingHours !== undefined || constraints.maxDrivingKm !== undefined) {
      if (travelInfo.drivingTimeHours === undefined && travelInfo.drivingDistanceKm === undefined) {
        return false; // Not driveable
      }
      
      if (constraints.maxDrivingHours !== undefined && travelInfo.drivingTimeHours !== undefined) {
        if (travelInfo.drivingTimeHours > constraints.maxDrivingHours) {
          return false;
        }
      }
      
      if (constraints.maxDrivingKm !== undefined && travelInfo.drivingDistanceKm !== undefined) {
        if (travelInfo.drivingDistanceKm > constraints.maxDrivingKm) {
          return false;
        }
      }
    }

    // Preference filtering
    if (constraints.preferDriving && travelInfo.drivingTimeHours === undefined) {
      return false;
    }

    return true;
  });
}
